/**
 * useChatSession — extracted from ConversationsChat.js (Session 2 refactor).
 * Jul 2026
 *
 * Owns the ~280-line "select an AvA → mount the chat" initialisation that
 * previously sat inline in `ConversationsChat.js`. Runs on `selectedAva.ava_id`
 * change:
 *   1. Creates a backend session (with optional `?challenge_id=…`)
 *   2. Loads any prior history from the SAME session (via getRecentConversations)
 *   3. Rebecca-anchor short-circuit (Rebecca stays silent until first STEM q)
 *   4. Fetches the AvA's intro via `/api/hibff-chat` with `[intro]`
 *   5. Renders greeting + fires background TTS with word-reveal
 *   6. Maya scripted-conversation engine (`/api/maya-manager/scripts/pending`)
 *   7. Fires the animated greeting-video moment
 *   8. Cleanup: clears TTS tracker + stops audio + resets reveal state
 *
 * Behaviour is byte-identical to the pre-extraction path. Deps list is
 * intentionally minimal (`selectedAva?.ava_id`) because expanding it would
 * re-run the entire greeting/history flow every time an unrelated field
 * (voice toggle, user record touch) changes — wiping conversation state
 * mid-session. See the inline comment at the effect's dep array below.
 */
import { useEffect } from 'react';
import { apiClient } from '../api/client';
import { devLog } from '../utils/devLogger';
import { TEEN_VOICES } from '../pages/avaLibrary/data';

export const useChatSession = ({
  selectedAva,
  user,
  interactionMode,
  isMuted,
  playTTS,
  stopAllAudio,
  setIsSpeaking,
  setMessages,
  setLoading,
  setCurrentSession,
  setHasGreeted,
  setAvaTyping,
  setRevealingMsgId,
  setRevealedWords,
  setShowGreetingVideo,
  greetedAvaRef,
  ttsPlayedIds,
  speechOwnerRef,
}) => {
  useEffect(() => {
    const initializeChat = async () => {
      if (!selectedAva || !selectedAva.ava_id) return;

      // Prevent double initialization (guards against StrictMode double-mount)
      if (greetedAvaRef.current === selectedAva.ava_id) return;
      greetedAvaRef.current = selectedAva.ava_id;

      // Iter47 fix — stale-request guard. If the user switches AvA while this
      // init is mid-flight, greetedAvaRef moves to the new id; every state
      // write below must bail so the old AvA's session/greeting can't clobber
      // the new one.
      const myAvaId = selectedAva.ava_id;
      const isStale = () => greetedAvaRef.current !== myAvaId;

      setLoading(true);
      setMessages([]);
      setHasGreeted(false);

      try {
        // CR-11: read optional ?challenge_id=… from the URL so the teen can
        // enter Practice "on" a specific 20-challenge. Backend will bind the
        // session to a new challenge_runs row (CR-2). Freestyle Practice is
        // unaffected.
        const urlChallengeId = new URLSearchParams(window.location.search).get('challenge_id');

        // Create session — if this fails, skip greeting but still show chat
        let session = null;
        try {
          const payload = {
            ava_id: selectedAva.ava_id,
            topic: `Chat with ${selectedAva.name}`,
          };
          if (urlChallengeId) payload.challenge_id = urlChallengeId;
          session = await apiClient.createSession(payload);
          if (isStale()) return;
          if (session?.session_id) {
            setCurrentSession(session);
          }
        } catch (e) {
          console.error('Session creation failed:', e);
        }

        // First greeting — fetched from the backend via the existing
        // /api/hibff-chat endpoint with a synthetic "[intro]" trigger.
        // Reuses the CR-3 challenge-context plumbing already in place:
        // when the session is bound to a challenge_run, the AvA opens with
        // an acknowledgement of that challenge.  When no run is bound,
        // freestyle Practice produces a warm AvA-identity intro.
        const firstName = user?.name?.split(' ')[0] || 'there';
        const avaLang = selectedAva?.avatar_config?.language || selectedAva?.language || 'en';

        // Load prior message history directly by AvA id — /api/hibff-chat
        // persists every turn with session_id == avatarId, so this is the
        // canonical key. (The previous getRecentConversations(10) gate
        // silently dropped ALL history whenever the AvA had no session in
        // the teen's 10 most-recent rows — e.g. Rebecca with 144 stored
        // messages showed an empty thread.)
        let isReturning = false;
        let previousMessages = [];
        try {
          const history = await apiClient.request(`/api/sessions/conversations/${selectedAva.ava_id}/messages`);
          if (isStale()) return;
          // Filter out synthetic placeholders saved by older schema —
          // these were never meant to be visible to the teen.
          const visibleFromApi = (history?.messages || []).filter(
            m => !(m.role === 'user' && /^\[(greeting|intro)\]$/i.test((m.content || '').trim())),
          );
          if (visibleFromApi.length > 0) {
            isReturning = true;
            previousMessages = visibleFromApi.map(m => ({
              id: m.message_id || m.conversation_id || `prev_${Math.random()}`,
              role: m.role === 'user' ? 'user' : 'ava',
              content: m.content,
              timestamp: new Date(m.timestamp || Date.now()),
              restored: true,
            }));
          }
        } catch (e) {
          // No message history available — start fresh
        }

        // Build the kickoff history seed: include any prior messages so
        // Claude can reference them, but cap to the most recent 8 turns
        // to keep latency low. Roles are normalised to user/assistant for
        // the backend.
        const seedHistory = previousMessages.slice(-8).map(m => ({
          role: m.role === 'user' ? 'user' : 'assistant',
          content: m.content,
        }));

        let greetingText = '';
        // Aug 2026 — Rebecca introduces herself like every other tutor.
        // The old "Rebecca stays NEUTRAL on entry" short-circuit (Feb 2026
        // model) skipped the [intro] request on first sessions, which meant
        // the backend's founder-authored first-session script could never
        // run through the UI. Removed: the [intro] request now always fires;
        // the backend decides (script on first session, silence afterwards).

        // Jun 2026 — trigger the lip-synced greeting + farewell clips FIRST,
        // before the intro TTS decision. The spoken text is CANONICAL (per
        // tutor + voice) so the backend cache generates each clip once and
        // replays it free. When the greeting clip is already cached (ready),
        // the CLIP carries the intro voice and we skip the intro TTS so the
        // tutor doesn't introduce herself twice.
        let greetingClipReady = false;
        let greetingTriggerStatus = null;
        // Thinking orb shows the moment the screen mounts — BEFORE the
        // awaited clip trigger (which can take seconds on a cache miss).
        setAvaTyping(true);
        // Fix C — the greeting script lives on the BACKEND (tutor_registry
        // .greeting_script): the same function renders this text, pre-generates
        // the name-first clip at onboarding/customisation, and keys the clip
        // cache. The bubble, the TTS audio and the lip-sync video therefore
        // render from ONE text source and can never diverge.
        let scriptedGreeting = null;
        try {
          const sr = await apiClient.request(
            `/api/ava-video/greeting-script/${selectedAva.ava_id}?returning=${isReturning ? 'true' : 'false'}`
          );
          scriptedGreeting = (sr && typeof sr.text === 'string' && sr.text.trim().length > 0) ? sr.text : null;
        } catch (e) {
          devLog('greeting-script fetch failed; using LLM intro path', e);
        }
        if (isStale()) return;
        if (session?.session_id && selectedAva?.ava_id) {
          const slot = TEEN_VOICES.find(v => v.id === (selectedAva?.avatar_config?.voice || ''));
          const momentVoice = slot?.baseVoice || selectedAva?.avatar_config?.voice || 'nova';
          const farewellText = 'That was a great session — I really enjoyed our time together. Come back soon and we\'ll pick up right where we left off!';
          const triggerMoment = (momentType, text) => apiClient.request('/api/ava-video/trigger-moment', {
            method: 'POST',
            body: JSON.stringify({
              session_id: session.session_id,
              moment_type: momentType,
              text,
              voice_id: momentVoice,
              ava_library_id: selectedAva.avatar_config?.ava_library_id || null,
              ava_id: selectedAva.ava_id,
            }),
          });
          if (scriptedGreeting) {
            try {
              const r = await triggerMoment('greeting', scriptedGreeting);
              greetingTriggerStatus = r?.status || null;
              greetingClipReady = r?.status === 'ready';
            } catch (e) { /* non-fatal */ }
          }
          triggerMoment('farewell', farewellText).catch(() => {});
          setShowGreetingVideo(true);
        }
        if (isStale()) return;

        if (scriptedGreeting) {
          // Fix B — SINGLE SOURCE OF TRUTH: the bubble displays the EXACT
          // string sent to the lip-sync trigger above and to TTS below.
          // The LLM [intro] is skipped so a longer LLM reply can never
          // desync the audio from the transcript again.
          greetingText = scriptedGreeting;
          setAvaTyping(false);
        } else {
          // (Orb already showing since mount — see setAvaTyping above.)
          try {
            const introResp = await apiClient.hibffChat(
              user?.user_id || 'anonymous',
              selectedAva?.ava_id || selectedAva?.name || 'default',
              'practice',
              '[intro]',
              seedHistory,
              avaLang,
              selectedAva?.name,
              session?.session_id || null,
            );
            if (introResp && typeof introResp.reply === 'string' && introResp.reply.trim().length > 0) {
              greetingText = introResp.reply;
            }
          } catch (e) {
            devLog('intro fetch failed; falling back to minimal template', e);
          }
          if (isStale()) return;
          setAvaTyping(false);
        }

        // Rebecca (Aug 2026): the backend deliberately returns an EMPTY
        // reply on her non-first sessions — she stays neutral on re-entry.
        // Don't inject the generic fallback greeting in that case.
        const isRebeccaAnchor = (
          (selectedAva?.avatar_config?.preset === 'rebecca_default')
          || (selectedAva?.name === 'Rebecca' && selectedAva?.is_fixed)
        );
        if (!greetingText && isRebeccaAnchor && isReturning) {
          setMessages(previousMessages);
          setHasGreeted(true);
          setLoading(false);
          return;
        }

        if (!greetingText) {
          // Resilience fallback only — never leaves the UI blank. Strictly
          // honours the tutor's language (no hardcoded-English greetings for
          // French/other tutors).
          const avaName = selectedAva?.name || 'your AvA';
          const FALLBACK_GREETINGS = {
            en: { ret: (n, a) => `Hi ${n} — great to see you again. I'm ${a}. Want to pick up where we left off?`, first: (n, a) => `Hi ${n} — I'm ${a}. I'm here when you're ready. What's on your mind?` },
            fr: { ret: (n, a) => `Salut ${n} — contente de te revoir ! Je suis ${a}. On reprend là où on s'était arrêtés ?`, first: (n, a) => `Salut ${n} — je suis ${a}. Je suis là quand tu es prêt. Qu'est-ce qui te préoccupe ?` },
            es: { ret: (n, a) => `¡Hola ${n} — qué alegría verte de nuevo! Soy ${a}. ¿Seguimos donde lo dejamos?`, first: (n, a) => `Hola ${n} — soy ${a}. Estoy aquí cuando quieras. ¿Qué tienes en mente?` },
            de: { ret: (n, a) => `Hallo ${n} — schön, dich wiederzusehen! Ich bin ${a}. Machen wir dort weiter, wo wir aufgehört haben?`, first: (n, a) => `Hallo ${n} — ich bin ${a}. Ich bin da, wenn du bereit bist. Was beschäftigt dich?` },
            tl: { ret: (n, a) => `Hi ${n} — masaya akong makita kang muli! Ako si ${a}. Ituloy natin kung saan tayo huminto?`, first: (n, a) => `Hi ${n} — ako si ${a}. Nandito ako kapag handa ka na. Ano ang nasa isip mo?` },
            pt: { ret: (n, a) => `Olá ${n} — que bom te ver de novo! Eu sou ${a}. Vamos continuar de onde paramos?`, first: (n, a) => `Olá ${n} — eu sou ${a}. Estou aqui quando estiver pronto. O que está na sua cabeça?` },
            ar: { ret: (n, a) => `مرحباً ${n} — سعيدة برؤيتك مجدداً! أنا ${a}. هل نكمل من حيث توقفنا؟`, first: (n, a) => `مرحباً ${n} — أنا ${a}. أنا هنا عندما تكون مستعداً. ما الذي يشغل بالك؟` },
            zh: { ret: (n, a) => `嗨 ${n} — 很高兴再见到你！我是${a}。我们接着上次继续吧？`, first: (n, a) => `嗨 ${n} — 我是${a}。我随时都在。你在想什么呢？` },
            ja: { ret: (n, a) => `${n}さん、また会えて嬉しいです！${a}です。前回の続きから始めましょうか？`, first: (n, a) => `${n}さん、はじめまして。${a}です。準備ができたら教えてね。何を考えていますか？` },
          };
          const pack = FALLBACK_GREETINGS[avaLang] || FALLBACK_GREETINGS.en;
          greetingText = isReturning ? pack.ret(firstName, avaName) : pack.first(firstName, avaName);
        }

        const greetingId = `greeting_${selectedAva.ava_id}`;
        const greetingMsg = {
          id: greetingId,
          role: 'ava',
          content: greetingText,
          timestamp: new Date(),
        };

        // Show previous messages + new greeting
        const allMessages = [...previousMessages, greetingMsg];

        if (interactionMode !== 'text' && !isMuted) {
          // Voice mode: the greeting text must NEVER appear before the voice.
          // Mask the bubble; the audio drives the word drip.
          setRevealingMsgId(greetingId);
          setRevealedWords(0);
          setMessages(allMessages);
          if (greetingClipReady) {
            // Branch 1 — the clip carries the voice. TTS stays disabled.
            if (speechOwnerRef) speechOwnerRef.current = 'clip';
          } else if (greetingTriggerStatus === 'processing' && session?.session_id) {
            // Fix D — STRICT PRE-PLAY HOLD GATE: idle loop + thinking orb for
            // up to 7s while the lip-sync clip finishes. No TTS, no word-drip.
            if (speechOwnerRef) speechOwnerRef.current = 'holding';
            setAvaTyping(true);
            const gateStart = Date.now();
            const poll = async () => {
              if (isStale()) return;
              let ready = false;
              try {
                const st = await apiClient.request(`/api/ava-video/moment-status/${session.session_id}/greeting`);
                ready = st?.status === 'ready' && !!st?.video_url;
              } catch (e) { /* keep waiting until the gate expires */ }
              if (isStale()) return;
              if (ready) {
                // Branch 1 (clip ready ≤7s) — useMomentVideo mounts the
                // overlay; browser TTS remains completely disabled.
                if (speechOwnerRef) speechOwnerRef.current = 'clip';
                setAvaTyping(false);
                return;
              }
              if (Date.now() - gateStart >= 7000) {
                // Branch 2 — SINGLE-SPEAKER INVARIANT: TTS owns this message.
                // A clip arriving at second 8 or 12 must NOT play or restart
                // anything (it is cached for replay/share only).
                if (speechOwnerRef) speechOwnerRef.current = 'tts';
                setAvaTyping(false);
                apiClient.request('/api/ava-video/moment-timeout', {
                  method: 'POST',
                  body: JSON.stringify({
                    ava_id: selectedAva.ava_id,
                    moment_type: 'greeting',
                    reason: 'clip_not_ready_7s',
                  }),
                }).catch(() => {});
                playTTS(greetingText, greetingId)
                  .catch((e) => devLog('greeting TTS background play failed', e));
                return;
              }
              setTimeout(poll, 1200);
            };
            poll();
          } else {
            // No clip possible (LLM intro path / no idle loop) — TTS speaks
            // the full text immediately, word drip synced to the audio.
            if (speechOwnerRef) speechOwnerRef.current = 'tts';
            playTTS(greetingText, greetingId)
              .catch((e) => devLog('greeting TTS background play failed', e));
          }
        } else {
          setMessages(allMessages);
        }
        setHasGreeted(true);

        // Sprint 207S — Maya scripted-conversation engine.
        // After the standard greeting, fetch any founder-authored scripts
        // for the `first_session` trigger that this teen hasn't seen yet
        // and stream them in order, marking each as delivered. Only fires
        // for the Maya AvA (detection mirrors avas.py admin gate).
        const isMaya = (
          (selectedAva?.avatar_config?.preset === 'maya_default')
          || (selectedAva?.name === 'Maya' && (selectedAva?.seeded_by || '').includes('maya'))
          || (selectedAva?.name === 'Maya' && selectedAva?.is_primary)
        );
        if (isMaya && !isReturning) {
          try {
            const pendingResp = await apiClient.request(
              '/api/maya-manager/scripts/pending?trigger=first_session',
            );
            const pending = pendingResp?.rows || [];
            for (const script of pending) {
              // Small pause so each message lands like a real Maya turn.
              await new Promise(r => setTimeout(r, 900));
              const scriptId = `maya_script_${script.script_id}`;
              const scriptMsg = {
                id: scriptId,
                role: 'ava',
                content: script.message,
                timestamp: new Date(),
              };
              setMessages(prev => [...prev, scriptMsg]);
              if (interactionMode !== 'text' && !isMuted) {
                // Sprint 207CC — fire TTS in BACKGROUND (don't await).
                // Lets the next scripted message land on schedule even
                // if OpenAI TTS is slow. Word reveal is TTS-synced inside
                // playTTS so the bubble drips in time with the audio
                // instead of dumping the whole script up front.
                setRevealingMsgId(scriptId);
                setRevealedWords(0);
                playTTS(script.message, scriptId).catch(() => {});
              }
              // Fire-and-forget delivery marker so we don't replay.
              apiClient
                .request(`/api/maya-manager/scripts/${script.script_id}/mark-delivered`, { method: 'POST' })
                .catch(() => {});
            }
          } catch (e) {
            devLog('maya scripted-conversation fetch failed (non-fatal)', e);
          }
        }

        // NOTE: the legacy [greeting] save call was removed — the backend
        // /api/hibff-chat [intro] short-circuit already persists the AvA
        // greeting to db.conversations (keyed by avatarId) so it appears
        // on the next resume.
      } catch (error) {
        console.error('Failed to initialize chat:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeChat();
    // Capture ref values at effect-run time so the cleanup doesn't read a
    // potentially-different ref later (React 18 StrictMode re-mount safety).
    const ttsRef = ttsPlayedIds.current;

    // Cleanup: stop audio, but do NOT reset greetedAvaRef (prevents StrictMode double-fire)
    return () => {
      ttsRef.clear();
      stopAllAudio();
      setIsSpeaking(false);
      setRevealingMsgId(null);
      setRevealedWords(0);
    };
    // Verified: this heavyweight chat init runs when the teen picks a new
    // AvA (keyed on the ava_id primitive). Adding `interactionMode`,
    // `isMuted`, `playTTS`, `stopAllAudio`, `user?.name`, `user?.user_id`
    // to deps would re-run the whole greeting/history reload every time
    // the teen toggles voice/text or their user record touches — wiping
    // conversation state and re-playing the intro. Session state MUST
    // survive those UI toggles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAva?.ava_id]);
};

export default useChatSession;
